import { DatePipe } from '@angular/common';
import { Component, forwardRef, HostListener, Injector, Input } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WebcamImage } from 'ngx-webcam';
import { StaffService } from 'src/app/core/api/staff.service';
import { DataSrcService } from '../enlargeable-media/data-src.service';
import { Attachment } from './attachments.component';
import { BaseAttachmentsService } from './base-attachments.service';
import { UrlWithContentType } from './s3-attachment.service';

const noop = () => {};
export interface AttachmentMetadata {
  key: string;
  [key: string]: any;
}

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BaseAttachmentsComponent),
  multi: true
};

@Component({
  template: '',
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR]
})
export class BaseAttachmentsComponent implements ControlValueAccessor {
  @Input() formControl: FormControl;
  @Input() accept = '';
  @Input() expandAttachments: boolean;
  @Input() maxAttachments: number;
  @Input() addFilesMessage: string = null;
  @Input() shouldStaySameHeight = false;
  @Input() metadata: AttachmentMetadata = { key: 'none' };
  @Input() preloadedAttachments: UrlWithContentType[] = [];
  @Input() usePreloadedAttachments: boolean;

  private datePipe: DatePipe;
  private dataSrcService: DataSrcService;
  public isReadOnly = false;

  attachments: Attachment[] = [];

  private onTouchedCallback: () => void = noop;
  protected onChangeCallback: (_: any) => void = noop;
  public cameraAccessEnabled = true; // By default everyone will have access
  //   this.staffService.isStaffPartOfGroup(
  //   this.staffService.staff || { cognitoGroups: null },
  //   CognitoGroup.CameraAccess
  // );

  @HostListener('dragover', ['$event'])
  onDragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }

  @HostListener('drop', ['$event'])
  onFileDrop(event) {
    event.preventDefault();

    if (event.dataTransfer.items) {
      this.addFiles(
        Array.from(event.dataTransfer.items).map((item: any) => item.getAsFile()) as File[]
      );
      event.dataTransfer.items.clear();
    } else {
      this.addFiles(event.dataTransfer.files);
      event.dataTransfer.clearData();
    }
  }

  constructor(
    protected injector: Injector,
    protected staffService: StaffService,
    protected baseAttachmentsService: BaseAttachmentsService
  ) {
    this.initializeServices();
  }

  private initializeServices(): void {
    this.datePipe = this.injector.get(DatePipe);
    this.dataSrcService = this.injector.get(DataSrcService);
  }

  ngOnInit() {
    if (this.metadata && this.metadata.key) {
      this.attachments =
        this.baseAttachmentsService.metadataKeyToAttachmentsMap[this.metadata.key] || [];
      this.updateControlValue();
      // this.formControl.patchValue(

      // );
    }
  }

  addImage(image: WebcamImage) {
    if (image) this.addFiles([this.dataURLtoFile(image)]);
  }

  dataURLtoFile(image: WebcamImage) {
    const filename = 'image_' + this.datePipe.transform(new Date(), 'yyyyMMdd_mm_ss');
    const arr = image.imageAsDataUrl.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  addFiles(files: File[] | FileList) {
    const filteredFiles = Array.from(files).filter(file => {
      return (
        file !== null &&
        (this.accept === '' ||
          this.accept.includes(file.type) ||
          this.accept.includes(file.name.match(/[a-z0-9]*$/i)[0]))
      );
    });

    Promise.all(this.convertMovToMp4(filteredFiles))
      .then(updatedFiles => {
        if (this.metadata) {
          updatedFiles.forEach(file => (file['metadata'] = JSON.stringify(this.metadata)));
        }
        this.updateAttachments(updatedFiles);
      })
      .catch(error => {
        console.error('Error:', error);
      });
  }

  updateAttachments(files: File[]) {
    this.attachments = this.attachments.concat(files).filter(attachment => attachment !== null);
    if (this.attachments.length > this.maxAttachments) {
      this.attachments.splice(0, this.attachments.length - this.maxAttachments);
    }
    this.updateControlValue();
  }

  removeAttachment(attachmentToRemove: Attachment) {
    this.attachments = this.attachments.filter(attachment => attachment !== attachmentToRemove);
    this.updateControlValue();
  }

  convertMovToMp4(files: File[]): (File | Promise<File>)[] {
    return files.map(file => {
      if (file.type === 'video/quicktime') {
        return this.dataSrcService.copyFile(file, true).then(fileAsMP4 => {
          return fileAsMP4;
        });
      } else {
        return file;
      }
    });
  }

  public writeValue(value: any) {
    this.attachments = value ? value : new Array<Attachment>();
  }

  public registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  public registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  public setDisabledState(isDisabled: boolean) {
    this.isReadOnly = isDisabled;
  }

  protected updateControlValue() {
    this.onChangeCallback(this.attachments);
    this.baseAttachmentsService.onAttachmentsUpdated(this.metadata, this.attachments);
  }
}
